import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../components/auth/AuthProvider';
import { motion } from 'framer-motion';
import { Trophy, Heart, Star, Sparkles } from 'lucide-react';

const ResultsView = ({ history, onRestart, onSaveAndContinue }) => {
    const buyList = history.filter(h => h.action === 'BUY_LIST');
    const watchlist = history.filter(h => h.action === 'SHORTLIST');
    const { login, user } = useAuth();

    const handleGoogleLogin = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            const success = await login(tokenResponse.access_token);
            if (success) {
                onSaveAndContinue();
            }
        },
        onError: () => console.error('Login Failed'),
    });

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-8">
            <motion.div
                className="max-w-5xl w-full bg-white rounded-[2.5rem] p-16"
                style={{
                    boxShadow: '0 40px 100px -20px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.05)'
                }}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
            >
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                    >
                        <Trophy className="w-20 h-20 mx-auto text-yellow-500 mb-6" strokeWidth={1.5} />
                    </motion.div>
                    <h1 className="text-6xl font-light text-gray-900 mb-4">Your Selections</h1>
                    <p className="text-gray-400 text-lg font-light">Here's what caught your eye</p>
                </div>

                <div className="grid md:grid-cols-2 gap-12 mb-12">
                    {/* Trade List */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center">
                                <Heart className="w-5 h-5 text-green-600" strokeWidth={1.5} />
                            </div>
                            <h2 className="text-2xl font-light text-gray-900">Trade List</h2>
                            <span className="text-2xl font-light text-gray-300">({buyList.length})</span>
                        </div>
                        <div className="space-y-3">
                            {buyList.length > 0 ? (
                                buyList.map((item, idx) => (
                                    <motion.div
                                        key={idx}
                                        className="px-6 py-4 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl border border-green-200"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.4 + idx * 0.05 }}
                                        whileHover={{ scale: 1.02, y: -2 }}
                                        style={{ boxShadow: '0 4px 12px -4px rgba(34, 197, 94, 0.3)' }}
                                    >
                                        <span className="text-xl font-light text-gray-900">{item.ticker}</span>
                                    </motion.div>
                                ))
                            ) : (
                                <div className="text-gray-400 text-sm font-light italic text-center py-8">
                                    No trades selected
                                </div>
                            )}
                        </div>
                    </motion.div>

                    {/* Watchlist */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-50 to-yellow-100 flex items-center justify-center">
                                <Star className="w-5 h-5 text-yellow-600" strokeWidth={1.5} />
                            </div>
                            <h2 className="text-2xl font-light text-gray-900">Watchlist</h2>
                            <span className="text-2xl font-light text-gray-300">({watchlist.length})</span>
                        </div>
                        <div className="space-y-3">
                            {watchlist.length > 0 ? (
                                watchlist.map((item, idx) => (
                                    <motion.div
                                        key={idx}
                                        className="px-6 py-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl border border-yellow-200"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.4 + idx * 0.05 }}
                                        whileHover={{ scale: 1.02, y: -2 }}
                                        style={{ boxShadow: '0 4px 12px -4px rgba(234, 179, 8, 0.3)' }}
                                    >
                                        <span className="text-xl font-light text-gray-900">{item.ticker}</span>
                                    </motion.div>
                                ))
                            ) : (
                                <div className="text-gray-400 text-sm font-light italic text-center py-8">
                                    No watchlist items
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>

                <motion.div
                    className="text-center pt-12 border-t border-gray-100"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                >
                    <div className="flex flex-col items-center gap-4">
                        {user ? (
                            <button
                                onClick={onSaveAndContinue}
                                className="px-12 py-4 bg-gray-900 text-white rounded-full font-light text-xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-3"
                            >
                                <Sparkles className="w-6 h-6 text-yellow-400" />
                                Save & View Dashboard
                            </button>
                        ) : (
                            <button
                                onClick={() => handleGoogleLogin()}
                                className="px-12 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full font-light text-xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-3"
                            >
                                <svg className="w-6 h-6" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z" />
                                </svg>
                                Save Watchlist
                            </button>
                        )}

                        <button
                            onClick={onRestart}
                            className="mt-4 px-8 py-2 text-gray-400 hover:text-gray-600 font-light text-sm hover:bg-gray-100 rounded-full transition-all"
                        >
                            Start New Session (Don't Save)
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default ResultsView;
